<template>
  <div
    id="media-tab-content-wrapper"
    class="media-tab"
    :class="{
      'bg-light-inverse': !isDarkMode,
      'bg-light': isDarkMode,
    }"
    :style="{ height: tabHeight }"
  >
    <div v-if="opened" class="media-tab--section mb-3">
      <h6 class="media-tab--section-title">Image ({{ imageAttachments.length }})</h6>
      <masonry :cols="imageCols" :gutter="8">
        <div
          v-for="attachment in imageAttachments"
          :key="attachment.attachment_id"
          class="media-tab--section-item"
        >
          <ImageItem
            :attachmentId="attachment.attachment_id"
            :attachment="attachment"
            :processed="attachment.status === 'processed'"
          />
          <div class="d-flex justify-content-center mt-1">
            <span class="media-tab--section-item-timestamp text-dark bg-light-custom">
              {{ formatTimestamp(attachment.created_at_msg, "LT") }}
            </span>
          </div>
        </div>
      </masonry>
    </div>
    <div v-if="opened" class="media-tab--section mb-3">
      <h6 class="media-tab--section-title">Audio ({{ audioAttachments.length }})</h6>
      <masonry :cols="audioCols" :gutter="8">
        <div
          v-for="attachment in audioAttachments"
          :key="attachment.attachment_id"
          class="media-tab--section-item"
        >
          <MediaItem
            :attachmentId="attachment.attachment_id"
            :attachment="attachment"
            :processed="attachment.status === 'processed'"
          />
          <div class="d-flex justify-content-center mt-1">
            <span class="media-tab--section-item-timestamp text-dark bg-light-custom">
              {{ formatTimestamp(attachment.created_at_msg, "LT") }}
            </span>
          </div>
        </div>
      </masonry>
    </div>
    <div v-if="opened" class="media-tab--section mb-3">
      <h6 class="media-tab--section-title">Video ({{ videoAttachments.length }})</h6>
      <masonry :cols="videoCols" :gutter="8">
        <div
          v-for="attachment in videoAttachments"
          :key="attachment.attachment_id"
          class="media-tab--section-item"
        >
          <MediaItem
            :attachmentId="attachment.attachment_id"
            :attachment="attachment"
            :processed="attachment.status === 'processed'"
          />
          <div class="d-flex justify-content-center mt-1">
            <span class="media-tab--section-item-timestamp text-dark bg-light-custom">
              {{ formatTimestamp(attachment.created_at_msg, "LT") }}
            </span>
          </div>
        </div>
      </masonry>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import ImageItem from "../ImageItem";
import MediaItem from "../MediaItem";
import AudioItem from "../AudioItem";
import formatter from "../../mixins/formatter";

export default {
  name: "MediaTab",
  mixins: [formatter],
  components: {
    ImageItem,
    MediaItem,
    AudioItem,
  },
  props: {
    tabHeight: {
      type: String,
      default: "100%",
    },
    conversationMessages: {
      type: Array,
      default: () => [],
    },
    conversationAttachments: {
      type: Array,
      default: () => [],
    },
    opened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imageCols: 1,
      videoCols: 1,
      audioCols: 1,
    };
  },
  computed: {
    ...mapGetters("user", ["viewport", "isDarkMode"]),
    attachments() {
      return this.conversationAttachments
        .map((attachment) => {
          const message = this.conversationMessages.find(
            (message) => message.message_id === attachment.message_id
          );
          return {
            ...attachment,
            created_at_msg: message ? message.created_at : attachment.created_at,
          };
        })
        .sort((a, b) => a.created_at - b.created_at);
    },
    imageAttachments() {
      return this.attachments
        .filter((attachment) => this.getAttachmentType(attachment.content_type) === 'image');
    },
    audioAttachments() {
      return this.attachments
        .filter((attachment) => this.getAttachmentType(attachment.content_type) === 'audio' && attachment.content_type !== 'audio/pcm');
    },
    videoAttachments() {
      return this.attachments
        .filter((attachment) => this.getAttachmentType(attachment.content_type) === 'video');
    },
  },
  methods: {
    getAttachmentType(contentType) {
      if (contentType) {
        contentType = contentType.toLowerCase();
        if (contentType.includes("image")) {
          return 'image';
        } else if (contentType.includes("video")) {
          return 'video';
        } else if (contentType.includes("audio")) {
          return 'audio';
        }
      }
      return "unknown";
    },
    getMasonryCols({type, width}) {
      if (type === 'audio') {
        if (width > 0) {
          const column = width / 308;
          return column >= 1 ? Math.floor(column) : Math.ceil(column);
        }
      } else {
        if (width > 0) {
          if (width < 400) {
            return 1;
          }
          const column = width / 208;
          return Math.floor(column);
        }
      }
      return 1;
    },
  },
  watch: {
    viewport: {
      handler(value) {
        if (this.opened) {
          const $ = window.$;
          const width = $('#media-tab-content-wrapper').width();
          this.videoCols = this.getMasonryCols({type: 'video', width});
          this.audioCols = this.getMasonryCols({type: 'audio', width});
          this.imageCols = this.getMasonryCols({type: 'image', width});
        }
      },
      deep: true,
    },
    opened: {
      handler(value) {
        if (value) {
          const $ = window.$;
          const width = $('#media-tab-content-wrapper').width();
          this.videoCols = this.getMasonryCols({type: 'video', width});
          this.audioCols = this.getMasonryCols({type: 'audio', width});
          this.imageCols = this.getMasonryCols({type: 'image', width});
        }
      },
      immediate: true,
    }
  },
};
</script>

<style lang="scss">
.media-tab {
  width: 100%;
  overflow: hidden;
  overflow-y: auto;
  padding: 8px;
  border-top: none !important;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;

  &--section {
    &-item {
      border-radius: 4px;
      margin-bottom: .75rem;
      .image__wrapper, .media_wrapper {
        video {
          border-radius: 4px;
        }
      }
      &-timestamp {
        padding: 2px 4px;
        font-size: 12px;
        font-weight: 500;
        border-radius: 2px;
      }
    }

  }
}
</style> 